'use client';

import { useState } from 'react';
import {
  AppUserFullInfo,
  postUpdateBusinessUserCookiePreferences,
  postUpdateUserCookiePreferences,
  useHandleGqlErrorsWithSnackbar,
  UserTypes,
} from '@bts-web/core-features/common';
import { css, Z_INDEX_LIBRARY } from '@bts-web/utils-style-engine';
import { initDataDogRum } from '@bts-web/utils-next-infrastructure';
import { useUserInfoContext } from '../../common';
import { MainConsent } from '../MainConsent/MainConsent';
import { ManageConsent } from '../ManageConsent/ManageConsent';
import {
  CookiesConsentTranslationsKeys,
  getCookiesConsentTranslations,
} from '../utils/getCookiesConsentTranslations';
import { getAppConfig } from '@bts-web/core-app-config';
import { Language, useClientTranslation } from '@bts-web/utils-lokalise';
import {
  UpdateBusinessUserMutation$data,
  UpdateUserMutation$data,
} from '@bts-web/data-layer/server';

const appConfig = getAppConfig();

const wrapperStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'large',
  position: 'fixed',
  borderRadius: '24px',
  borderBottomLeftRadius: {
    base: 0,
    lg: '24px',
  },
  borderBottomRightRadius: {
    base: 0,
    lg: '24px',
  },
  bottom: {
    base: 0,
    lg: 'medium',
  },
  right: {
    base: 0,
    lg: 'medium',
  },
  width: {
    base: '100%',
    lg: '485px',
  },
  backgroundColor: 'neutrals.fill_quinary',
  paddingX: 'medium',
  paddingTop: 'large',
  paddingBottom: 'medium',
  pointerEvents: 'auto',
});

export const titleStyles = css({
  color:
    appConfig?.appId === 4 ? 'brand.text_primary' : 'neutrals.text_primary',
  fontFamily: 'headline.medium_medium',
  fontSize: 'headline.medium_medium',
  fontWeight: 'headline.medium_medium',
  lineHeight: 'headline.medium_medium',
  letterSpacing: 'headline.medium_medium',
  textAlign: 'center',
});

export const paragraphStyles = css({
  color: 'neutrals.text_primary',
  fontFamily: 'label.medium_medium',
  fontSize: 'label.medium_medium',
  fontWeight: 'label.medium_medium',
  lineHeight: 'label.medium_medium',
  letterSpacing: 'label.medium_medium',
});

export const linkStyles = css({
  color: 'brand.text_primary',
  fontFamily: 'label.medium_bold',
  fontSize: 'label.medium_bold',
  fontWeight: 'label.medium_bold',
  lineHeight: 'label.medium_bold',
  letterSpacing: 'label.medium_bold',
});

export type NonEssentialCookiesConsent = Omit<
  AppUserFullInfo['cookiePreferences'],
  'functional'
>;

export interface CommonConsentScreenProps {
  updateCookiePreferences: (newPreferences: NonEssentialCookiesConsent) => void;
  translations: CookiesConsentTranslationsKeys;
  language: Language;
}

interface CookiesConsentBanner {
  userType: UserTypes;
  language: Language;
  cookiePreferences: AppUserFullInfo['cookiePreferences'];
}

const CookiesConsentBanner = ({
  cookiePreferences,
  userType,
  language,
}: CookiesConsentBanner) => {
  const { updateUserData } = useUserInfoContext();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { t } = useClientTranslation();

  const translations = getCookiesConsentTranslations(t);

  const { performance, marketing, functional } = cookiePreferences;

  const [isManagingCookies, setIsManagingCookies] = useState(false);

  const [isHidden, setIsHidden] = useState(functional);

  const updateUserCookiePreferencesAction =
    userType === UserTypes.BUSINESS_USER
      ? postUpdateBusinessUserCookiePreferences
      : postUpdateUserCookiePreferences;

  const onClickManage = () => {
    setIsManagingCookies(true);
  };

  const onClickBackToMain = () => {
    setIsManagingCookies(false);
  };

  const updateCookiePreferences = async (
    newPreferences: NonEssentialCookiesConsent,
  ) => {
    setIsHidden(true);

    try {
      const userUpdateResponse =
        await updateUserCookiePreferencesAction(newPreferences);

      if (userUpdateResponse.errors) {
        throw new Error(
          `Failed to update cookie preferences from banner: ${JSON.stringify(
            userUpdateResponse.errors,
          )}`,
        );
      }

      if (userUpdateResponse.data) {
        const updatedPreferences =
          userType === UserTypes.BUSINESS_USER
            ? (userUpdateResponse.data as UpdateBusinessUserMutation$data)
                .updateBusinessUser?.cookiePreferences
            : (userUpdateResponse.data as UpdateUserMutation$data).updateUser
                ?.cookiePreferences;

        if (updatedPreferences) {
          initDataDogRum({
            cookiePreferences: updatedPreferences,
          });

          updateUserData({ cookiePreferences: updatedPreferences });
        }
      }
    } catch (_error) {
      processErrors(['err_cookie_settings_update']);

      setIsHidden(false);
    }
  };

  if (isHidden === true) {
    return null;
  }

  return (
    <div
      className={wrapperStyles}
      style={{
        zIndex: Z_INDEX_LIBRARY.COOKIE_BANNER,
      }}
    >
      {isManagingCookies ? (
        <ManageConsent
          cookiePreferences={{ performance, marketing }}
          translations={translations}
          language={language}
          onClickBackToMain={onClickBackToMain}
          updateCookiePreferences={updateCookiePreferences}
        />
      ) : (
        <MainConsent
          translations={translations}
          language={language}
          onClickManage={onClickManage}
          updateCookiePreferences={updateCookiePreferences}
        />
      )}
    </div>
  );
};

export { CookiesConsentBanner };
